// Users resource — minimal frontend surface for the /api/users CRUD
// endpoints. v1 only exposes list + role-update because that's the
// concrete need: an admin (or self-service) way to assign firm roles
// so the priority queue actually has work routed to a user.
//
// Add/edit/delete UIs can land when the firm needs them; goat already
// supports them server-side.

import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import { halFetch, type HalCollection, type HalResource } from "~/lib/hal";
import { AUTH_ME_QUERY_KEY } from "~/lib/auth";
import { TASKS_LIST_KEY } from "~/lib/tasks";

/** A User row returned by GET /api/users. Mirrors models.User. */
export type User = HalResource<{
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  /** Auth tier — super / admin / user. NOT the firm role. */
  type: string;
  /** Firm-task role driving the priority queue routing. */
  role: string;
  external_id?: string;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}>;

export type UsersCollection = HalCollection<User>;

/**
 * Sentinel email of the singleton system user (seeded server-side as
 * bootstrap.SystemUserEmail). It's plumbing for queue-driven tasks and
 * the client_credentials flow, so the admin roster excludes it via the
 * resource's `email_ne` filter — matching on email rather than auth
 * tier keeps human super-admins visible and role-assignable.
 */
export const SYSTEM_USER_EMAIL = "system@mayfirm.local";

// ── Queries ───────────────────────────────────────────────────────────

export const USERS_LIST_KEY = ["users"] as const;

export const usersListQueryOptions = queryOptions<UsersCollection>({
  queryKey: USERS_LIST_KEY,
  queryFn: ({ signal }) => {
    // Exclude the singleton system user server-side via the not-equal
    // email filter, so it never reaches the roster the admin renders.
    const params = new URLSearchParams({ email_ne: SYSTEM_USER_EMAIL });
    return halFetch<UsersCollection>(`/api/users?${params.toString()}`, {
      signal,
    });
  },
});

// ── Mutations ─────────────────────────────────────────────────────────

interface UpdateUserRoleVariables {
  id: string;
  role: string; // "" = unassigned sentinel; valid firm-role string otherwise
}

/**
 * Update a user's firm role. The backend's UserRequest is a partial
 * patch — sending only `role` keeps every other field untouched.
 *
 * On success: invalidates the users list, the auth /me query, AND
 * the queue. The queue is server-rendered against the user's role,
 * so a role change must drop the cached queue or the home page will
 * keep showing tasks routed for the previous role until the cache
 * naturally lapses.
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, role }: UpdateUserRoleVariables) =>
      halFetch<User>(`/api/users/${id}`, {
        method: "PUT",
        body: { role },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TASKS_LIST_KEY });
    },
  });
}
